import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import PasswordInput from '../../components/PasswordInput';
import Captcha from '../../components/Captcha';
import { resolveMedia, useSettings } from '../../context/SettingsContext';
import { DEFAULT_LOGO } from '../../config/brand';

export default function Login() {
  const { settings } = useSettings();
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({
    email: localStorage.getItem('ca_saved_email') || '',
    password: '',
  });
  const [rememberMe, setRememberMe] = useState(Boolean(localStorage.getItem('ca_saved_email')));
  const [agreeTerms, setAgreeTerms] = useState(true);
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [errors, setErrors] = useState({});

  const submit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!form.email.trim()) {
      newErrors.email = 'Please enter your email address';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!form.password) {
      newErrors.password = 'Please enter your account password';
    }

    if (!isCaptchaValid) {
      newErrors.captcha = 'Please enter the exact 5-character Captcha code';
    }

    if (!agreeTerms) {
      newErrors.agree = 'You must accept the Terms of Service & Privacy Policy';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fill in all highlighted required fields');
      return;
    }

    setErrors({});

    try {
      await login(form.email, form.password);
      if (rememberMe) {
        localStorage.setItem('ca_saved_email', form.email);
      } else {
        localStorage.removeItem('ca_saved_email');
      }
      toast.success('Welcome back to Chandana Aura!');
      const redirectTo = location.state?.from || '/';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed. Please verify your credentials.');
    }
  };

  return (
    <div className="min-h-[70vh] bg-[#FAF8F5] pt-3 pb-8 sm:pt-5 sm:pb-12 px-4 flex items-center justify-center text-[#2B1810]">
      <div className="w-full max-w-4xl bg-white border border-[#E8DFD8] rounded-2xl shadow-xl overflow-hidden grid md:grid-cols-12">
        
        {/* Left Visual Brand Column with Generated Sandalwood Art */}
        <div className="hidden md:flex md:col-span-5 relative text-[#FAF8F5] p-6 sm:p-7 flex-col justify-between overflow-hidden min-h-[520px]">
          {/* High-res Sandalwood Deity Idol Image */}
          <img
            src="/assets/auth_login_banner.jpg"
            alt="Authentic Sandalwood Artifacts"
            className="absolute inset-0 w-full h-full object-cover object-center transform scale-105 transition-transform duration-1000 hover:scale-110"
          />
          {/* Luxury Gradient Dark Overlays for Ultra-Clear Text Contrast */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C100A] via-[#1C100A]/60 to-[#1C100A]/75 backdrop-blur-[1.5px]" />
          <div className="absolute inset-0 bg-[radial-gradient(#C49A45_1px,transparent_1px)] [background-size:20px_20px] opacity-15" />

          {/* Top Brand Link & Badges */}
          <div className="relative z-10 space-y-4">
            <Link to="/" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/10 hover:border-[#C49A45] transition-all group" aria-label="Back to store">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#C49A45] group-hover:underline flex items-center gap-1.5">
                <span>←</span> Back to Store
              </span>
            </Link>

            <div className="space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#C49A45] bg-[#C49A45]/20 backdrop-blur-md px-2.5 py-0.5 rounded-full border border-[#C49A45]/50 inline-block shadow-sm">
                100% Pure Sandalwood
              </span>
              <h2 className="font-heading text-xl sm:text-2xl font-bold leading-snug text-white drop-shadow-md">
                Authentic Sacred Artifacts & Divine Aromas
              </h2>
              <p className="text-xs text-[#E5D7CC] leading-relaxed drop-shadow-sm font-medium">
                Log in to track your sacred orders, manage your wishlist, and unlock exclusive devotee loyalty rewards.
              </p>
            </div>
          </div>

          {/* Trust Highlights Bottom Panel */}
          <div className="relative z-10 space-y-2 pt-4 border-t border-white/15 bg-black/30 backdrop-blur-md p-3.5 rounded-xl border border-white/10 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-[#C49A45]/30 border border-[#C49A45] text-[#C49A45] flex items-center justify-center font-bold text-xs shrink-0 shadow-inner">
                ✓
              </div>
              <div className="text-xs">
                <p className="font-bold text-white leading-tight">Govt. Lab Certified</p>
                <p className="text-[10px] text-[#CFC3BA]">100% Genuine Mysore Sandalwood</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-[#C49A45]/30 border border-[#C49A45] text-[#C49A45] flex items-center justify-center font-bold text-[11px] shrink-0 shadow-inner">
                🔒
              </div>
              <div className="text-xs">
                <p className="font-bold text-white leading-tight">256-Bit Encrypted Security</p>
                <p className="text-[10px] text-[#CFC3BA]">Safe, Verified & Confidential</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Form Column */}
        <div className="md:col-span-7 p-5 sm:p-7 flex flex-col justify-center">
          {/* Header */}
          <div className="text-center md:text-left mb-4">
            <Link to="/" className="inline-block md:hidden mb-2">
              <img
                src={resolveMedia(settings?.logo || DEFAULT_LOGO)}
                alt={settings?.siteName || 'Chandana Aura'}
                className="h-9 mx-auto object-contain"
              />
            </Link>
            <h1 className="font-heading text-2xl font-bold text-[#2B1810]">
              Welcome Back
            </h1>
            <p className="text-xs text-[#8A796C] mt-0.5">
              Sign in to continue to your Chandana Aura account
            </p>
          </div>

          {/* Quick Tab Switcher */}
          <div className="flex border-b border-[#E8DFD8] mb-4">
            <button
              type="button"
              className="pb-2 font-bold text-xs uppercase tracking-wider text-[#C49A45] border-b-2 border-[#C49A45]"
            >
              Sign In
            </button>
            <Link
              to="/register"
              className="pb-2 px-6 font-bold text-xs uppercase tracking-wider text-[#8A796C] hover:text-[#2B1810] border-b-2 border-transparent transition-colors"
            >
              Create Account
            </Link>
          </div>

          {/* Login Form with noValidate */}
          <form onSubmit={submit} noValidate className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#2B1810] mb-1.5">
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="your.email@example.com"
                  className={`w-full h-11 px-3.5 pl-10 text-xs rounded-lg border transition-all font-medium ${
                    errors.email
                      ? 'border-red-500 bg-red-50/20 text-red-900 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                      : 'border-[#D9D0C8] bg-[#FAF8F5] text-[#2B1810] placeholder-[#A0938A] focus:bg-white focus:border-[#C49A45] focus:ring-1 focus:ring-[#C49A45]'
                  } focus:outline-none`}
                  value={form.email}
                  onChange={(e) => {
                    setForm({ ...form, email: e.target.value });
                    if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
                  }}
                />
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8A796C]">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                </span>
              </div>
              {errors.email && (
                <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                  <span>⚠</span> {errors.email}
                </p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-bold text-[#2B1810]">
                  Password <span className="text-red-500">*</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-[11px] font-bold text-[#C49A45] hover:underline"
                >
                  Forgot Password?
                </Link>
              </div>
              <PasswordInput
                autoComplete="current-password"
                placeholder="••••••••"
                value={form.password}
                error={Boolean(errors.password)}
                onChange={(e) => {
                  setForm({ ...form, password: e.target.value });
                  if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
                }}
              />
              {errors.password && (
                <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                  <span>⚠</span> {errors.password}
                </p>
              )}
            </div>

            {/* Captcha Security Check */}
            <div className="pt-1">
              <Captcha
                error={Boolean(errors.captcha)}
                onValidate={(valid) => {
                  setIsCaptchaValid(valid);
                  if (valid && errors.captcha) setErrors((prev) => ({ ...prev, captcha: '' }));
                }}
              />
              {errors.captcha && (
                <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                  <span>⚠</span> {errors.captcha}
                </p>
              )}
            </div>

            {/* Remember Me & Terms Agreement */}
            <div className="space-y-2.5 pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-[#D9D0C8] text-[#C49A45] focus:ring-[#C49A45] cursor-pointer accent-[#C49A45]"
                />
                <span className="text-xs text-[#54453D] font-medium">Remember my email on this device</span>
              </label>

              <div>
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) => {
                      setAgreeTerms(e.target.checked);
                      if (e.target.checked && errors.agree) setErrors((prev) => ({ ...prev, agree: '' }));
                    }}
                    className={`w-4 h-4 mt-0.5 rounded border text-[#C49A45] focus:ring-[#C49A45] cursor-pointer accent-[#C49A45] ${
                      errors.agree ? 'border-red-500 ring-1 ring-red-500' : 'border-[#D9D0C8]'
                    }`}
                  />
                  <span className="text-xs text-[#54453D]">
                    I agree to Chandana Aura's{' '}
                    <Link to="/terms" target="_blank" className="font-bold text-[#C49A45] hover:underline">
                      Terms of Service
                    </Link>{' '}
                    &{' '}
                    <Link to="/privacy-policy" target="_blank" className="font-bold text-[#C49A45] hover:underline">
                      Privacy Policy
                    </Link>
                  </span>
                </label>
                {errors.agree && (
                  <p className="text-[11px] font-semibold text-red-600 mt-1 pl-6 flex items-center gap-1">
                    <span>⚠</span> {errors.agree}
                  </p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#2B1810] hover:bg-[#C49A45] text-white font-bold text-xs uppercase tracking-widest rounded-lg shadow-md hover:shadow-lg transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <span>LOGGING IN...</span>
              ) : (
                <>
                  <span>SIGN IN TO ACCOUNT</span>
                  <span>→</span>
                </>
              )}
            </button>
          </form>

          {/* Footer Link */}
          <p className="text-xs text-center text-[#8A796C] mt-6 pt-4 border-t border-[#E8DFD8]">
            Don't have an account yet?{' '}
            <Link to="/register" className="text-[#C49A45] font-bold hover:underline">
              Create a free account
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
}
